import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import useMediaQuery from "@mui/material/useMediaQuery";
import { toast } from "react-toastify";
import { useAdminAuth } from "../../hooks/useAdminAuth";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TextField from "@mui/material/TextField";
import GoogleDriveSettings from "./GoogleDriveSettings";
import CloudIcon from '@mui/icons-material/Cloud';

import { useDispatch, useSelector } from "react-redux";
import { fetchStaffs } from "./staffSlice";
import { createStaffAccount } from "./onboardingSlice";
import { checkDriveStatus, getDriveAuthUrl, unlinkDrive } from "../../utils/driveApi";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const StaffList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    // Check if user is admin (phone auth) - use flag from authSlice
    const isAdmin = user?.isPhoneUser || false;
    // Use Redux state for staff list and loading
    const { staffs: staffList, loading } = useSelector((state) => state.staff);
    const { deleteStaff, loading: authLoading } = useAdminAuth();

    const [currentPage, setCurrentPage] = useState(1);
    const [showAddStaffModal, setShowAddStaffModal] = useState(false);

    // Add Staff Modal State - Create new staff account
    const [staffBranchName, setStaffBranchName] = useState('');
    const [staffEmailInput, setStaffEmailInput] = useState('');
    const [staffPasswordInput, setStaffPasswordInput] = useState('');
    const [staffWhatsApp, setStaffWhatsApp] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [creatingLoading, setCreatingLoading] = useState(false);
    const [createError, setCreateError] = useState('');

    // Success dialog state
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [createdStaffInfo, setCreatedStaffInfo] = useState(null);
    const [sendingInvitations, setSendingInvitations] = useState(false);
    const [isEmailLoading, setIsEmailLoading] = useState(false);

    // Delete confirmation dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [staffToDelete, setStaffToDelete] = useState(null);

    const isTinyScreen = useMediaQuery('(max-height:720px)');
    const isShortScreen = useMediaQuery('(max-height:900px)');
    const isMediumScreen = useMediaQuery('(max-height:1080px)');
    const itemsPerPage = isTinyScreen ? 4 : isShortScreen ? 5 : isMediumScreen ? 6 : 8;

    // Google Drive state
    const [driveLinked, setDriveLinked] = useState(false);
    const [driveLoading, setDriveLoading] = useState(false);
    const [driveDisconnectDialogOpen, setDriveDisconnectDialogOpen] = useState(false);

    // TextField styles matching UnifiedPolicyForm
    const textFieldStyles = {
        width: "100%",
        "& .MuiInputBase-root": {
            height: isTinyScreen ? "44px" : "56px",
            borderRadius: "8px",
            borderWidth: "1px",
            fontSize: "14px",
            backgroundColor: "#FFFFFF",
            "& fieldset": {
                borderColor: "#E0E0E0",
                borderWidth: "1px",
            },
            "&:hover fieldset": {
                borderColor: "#BDBDBD",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#0063F7",
                borderWidth: "2px",
            },
            "&.Mui-error fieldset": {
                borderColor: "#d32f2f",
                borderWidth: "2px",
            },
        },
        "& .MuiInputBase-input": {
            padding: isTinyScreen ? "10px 12px" : "16px 12px",
            height: isTinyScreen ? "20px" : "24px",
            display: "flex",
            alignItems: "center",
        },
        "& .MuiInputLabel-root": {
            fontSize: "14px",
            color: "#757575",
            "&.Mui-focused": {
                fontSize: "14px",
                color: "#0063F7",
            },
            "&.Mui-error": {
                color: "#d32f2f",
            },
        },
        "& .MuiFormHelperText-root": {
            margin: "4px 0 0 0",
            fontSize: "12px",
            "&.Mui-error": {
                color: "#d32f2f",
            },
        }
    };

    useEffect(() => {
        if (user?.uid) {
            dispatch(fetchStaffs(user.uid));
        }
    }, [dispatch, user?.uid]);

    // loadStaffList is no longer needed as we use Redux state
    const refreshStaffList = () => {
        if (user?.uid) {
            dispatch(fetchStaffs(user.uid));
        }
    };


    // Check Drive status on mount
    useEffect(() => {
        const loadDriveStatus = async () => {
            if (!isAdmin || !user?.uid) return;
            try {
                const status = await checkDriveStatus();
                setDriveLinked(status.linked);
            } catch (err) {
                console.error('Error checking Drive status:', err);
            }
        };
        loadDriveStatus();
    }, [isAdmin, user?.uid]);

    const handleConnectDrive = async () => {
        if (driveLinked) {
            setDriveDisconnectDialogOpen(true);
            return;
        }

        try {
            setDriveLoading(true);
            const authUrl = await getDriveAuthUrl('desktop');
            window.location.href = authUrl;
        } catch (err) {
            console.error('Error connecting Drive:', err);
            toast.error('Failed to connect Google Drive');
            setDriveLoading(false);
        }
    };

    const handleDisconnectDrive = async () => {
        try {
            setDriveLoading(true);
            await unlinkDrive();
            setDriveLinked(false);
            setDriveDisconnectDialogOpen(false);
            toast.success('Successfully disconnected Google Drive', {
                containerId: 'bottom-center',
                position: 'bottom-center',
                autoClose: 2000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: false,
                draggable: false,
                closeButton: false,
                style: {
                    backgroundColor: '#166434',
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 500,
                    padding: '12px 16px',
                    minHeight: '44px',
                    alignItems: 'center',
                    gap: '10px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    marginBottom: window.innerWidth <= 1024 ? '75vh' : '20px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    maxWidth: 'none',
                    display: 'inline-flex',
                },
                icon: (
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ color: '#FFFFFF' }}
                    >
                        <path
                            d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                ),
            });
        } catch (err) {
            console.error('Error disconnecting Drive:', err);
            toast.error('Failed to disconnect Google Drive');
        } finally {
            setDriveLoading(false);
        }
    };

    const handleCreateStaff = async () => {
        setCreateError('');

        if (staffList.length >= 5) {
            setCreateError('Limit Exceeded: You can only create up to 5 staff accounts.');
            return;
        }

        if (!staffBranchName.trim()) {
            setCreateError('Please enter a branch name');
            return;
        }

        if (!staffEmailInput.trim()) {
            setCreateError('Please enter a staff email address');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(staffEmailInput)) {
            setCreateError('Please enter a valid email address');
            return;
        }

        if (!staffPasswordInput || staffPasswordInput.length < 6) {
            setCreateError('Password must be at least 6 characters');
            return;
        }

        if (staffWhatsApp.trim() && !/^\d{10}$/.test(staffWhatsApp.trim())) {
            setCreateError('WhatsApp number must be exactly 10 digits');
            return;
        }

        setCreatingLoading(true);
        try {
            const result = await dispatch(createStaffAccount({
                branchName: staffBranchName.trim(),
                email: staffEmailInput.trim(),
                password: staffPasswordInput,
                whatsappNumber: staffWhatsApp.trim() || ''
            })).unwrap();

            // Store created staff info for success dialog
            setCreatedStaffInfo({
                branchName: staffBranchName.trim(),
                email: staffEmailInput.trim(),
                password: staffPasswordInput,
            });

            // Reset form and close modal
            setStaffBranchName('');
            setStaffEmailInput('');
            setStaffPasswordInput('');
            setStaffWhatsApp('');
            setShowAddStaffModal(false);
            setSuccessDialogOpen(true);
            refreshStaffList();
        } catch (err) {
            console.error('Error creating staff:', err);
            if (err.message?.includes('already')) {
                setCreateError('This email is already in use. Please use a different email.');
            } else if (err.message?.includes('Trial')) {
                setCreateError('Your trial period has expired. Please upgrade to add staff.');
            } else {
                setCreateError(err.message || 'Failed to create staff account');
            }
        } finally {
            setCreatingLoading(false);
        }
    };

    const handleShareCredentials = async () => {
        if (!createdStaffInfo) return;

        setSendingInvitations(false);
        setIsEmailLoading(true);

        // Hide loader and show "Sent" state after 3 seconds (optimistic UI)
        setTimeout(() => {
            setIsEmailLoading(false);
            setSendingInvitations(true);
        }, 3000);

        try {
            const { auth } = await import('../../firebase/firebaseConfig');
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error('No authenticated user');

            const token = await currentUser.getIdToken();
            const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const backendUrl = isLocal ? 'http://localhost:5001' : (import.meta.env.VITE_BACKEND_URL || 'http://localhost:5001');

            const response = await fetch(`${backendUrl}/api/send-credentials`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    adminName: user?.displayName || 'Admin',
                    staffList: [{
                        email: createdStaffInfo.email,
                        password: createdStaffInfo.password,
                        branchName: createdStaffInfo.branchName,
                    }],
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to send emails');
            }

            toast.success("Credentials sent to staff member!");
        } catch (err) {
            console.error('Error sending credentials:', err);
            toast.error(err.message || 'Failed to send credential emails');
            setSendingInvitations(false);
        }
    };

    const handleDeleteStaff = async () => {
        if (!staffToDelete) return;

        try {
            await deleteStaff(staffToDelete.uid);
            toast.success("Staff member deleted successfully", {
                containerId: "bottom-center",
                position: "bottom-center",
            });
            setDeleteDialogOpen(false);
            setStaffToDelete(null);
            refreshStaffList();
        } catch (err) {
            console.error('Error deleting staff:', err);
            toast.error(err.message || 'Failed to delete staff member');
        }
    };

    const paginated = staffList.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );
    const totalPages = Math.ceil(staffList.length / itemsPerPage);

    const rows = paginated.map((s, index) => {
        return {
            id: s.uid,
            index: (currentPage - 1) * itemsPerPage + index + 1,
            email: s.email,
            phone: s.phoneNumber || "-",
            linkedAt: s.linkedAt ? new Date(s.linkedAt).toLocaleDateString() : "-",
            original: s
        };
    });

    return (
        <Box sx={{ px: 2, pt: 1 }}>
            {/* Header with title and Add Staff button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontFamily: 'Roboto Slab',
                        fontWeight: 550,
                        fontSize: '32px',
                        fontStyle: 'Medium',
                        lineHeight: '56px',
                        letterSpacing: '0.02em',
                        opacity: 1,
                        width: 244,
                        height: 56,
                        textEmphasisColor: '#212121'
                    }}
                >
                    Staff
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {isAdmin && (
                        <Button
                            variant="contained"
                            startIcon={<CloudIcon />}
                            onClick={handleConnectDrive}
                            disabled={driveLoading}
                            sx={{
                                width: 230,
                                height: 48,
                                borderRadius: '8px',
                                p: '12px 12px 12px 16px',
                                gap: '8px',
                                boxShadow: 0,
                                backgroundColor: driveLinked ? '#FFEBEE !important' : '#E0EBFF !important',
                                color: driveLinked ? '#D32F2F !important' : '#0063F7 !important',
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '24px',
                                letterSpacing: '0.01em',
                                '&:hover': {
                                    backgroundColor: driveLinked ? '#FFCDD2 !important' : '#E0EBFF !important',
                                    color: driveLinked ? '#B71C1C !important' : '#0063F7 !important',
                                    boxShadow: 2
                                },
                                '&:disabled': {
                                    backgroundColor: '#F5F5F5 !important',
                                    color: '#BDBDBD !important',
                                }
                            }}
                        >
                            {driveLinked ? 'Disconnect Drive' : driveLoading ? 'Connecting...' : 'Connect Google Drive'}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<AddCircleIcon />}
                        onClick={() => setShowAddStaffModal(true)}
                        sx={{
                            backgroundColor: "#0063F7 !important",
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            px: 3,
                            "&:hover": { backgroundColor: "#0051d4 !important" }
                        }}
                    >
                        Create Staff
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    background: '#fff',
                    borderRadius: '0',
                    boxShadow: 'none',
                    px: { xs: 2, sm: 3 },
                    pb: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    gap: 0,
                    minHeight: 'calc(100vh - 120px)',
                    height: 'calc(100vh - 120px)',
                }}
            >
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', pt: 10, height: '100%' }}>
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            component={Paper}
                            sx={{
                                boxShadow: 'none',
                                border: 'none',
                                backgroundColor: 'transparent',
                                borderRadius: 0,
                                px: 0,
                                mx: { xs: -2, sm: -3 },
                                width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' },
                                maxHeight: 'none',
                                overflow: 'visible',
                                '& .MuiTableHead-root .MuiTableCell-root': {
                                    borderBottom: '1px solid #E0E0E0',
                                    backgroundColor: 'transparent',
                                },
                                '& .MuiTableBody-root .MuiTableCell-root': {
                                    borderBottom: '1px solid #E0E0E0',
                                    backgroundColor: 'transparent',
                                },
                                '& .MuiTableRow-root': {
                                    borderRadius: '0 !important',
                                },
                                '& .MuiTableCell-root': {
                                    borderRadius: '0 !important',
                                },
                                '& .MuiPaper-root': {
                                    borderRadius: '0 !important',
                                    boxShadow: 'none',
                                    border: 'none',
                                },
                            }}
                        >
                            <Table
                                size="medium"
                                aria-label="staff table"
                                sx={{
                                    border: 'none',
                                    borderCollapse: 'collapse',
                                    tableLayout: 'fixed',
                                    width: '100%',
                                    '& th, & td': {
                                        border: 'none',
                                        px: 0,
                                    },
                                    '& td:first-of-type, & th:first-of-type': {
                                        pl: 4,
                                    },
                                    '& td:last-of-type, & th:last-of-type': {
                                        pr: 4,
                                    },
                                    '& tr': {
                                        borderBottom: '1px solid #E0E0E0',
                                    },
                                }}
                            >
                                <TableHead>
                                    <TableRow sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                        <TableCell sx={{ width: '10%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span style={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', color: '#4B5563DE' }}>#</span>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '30%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <PersonIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', color: '#4B5563DE' }}>Staff</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '25%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <DescriptionIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', color: '#4B5563DE' }}>Phone</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '25%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <AccessTimeIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                                                <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', color: '#4B5563DE' }}>Linked At</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ width: '10%', textAlign: 'right' }}>
                                            <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '16px', color: '#4B5563DE' }}>Actions</Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 ? (
                                        <TableRow sx={{ height: 48 }}>
                                            <TableCell colSpan={5} align="center">
                                                <Typography sx={{ color: '#6B7280', py: 2 }}>No staff members found.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        rows.map((row) => (
                                            <TableRow
                                                key={row.id}
                                                hover
                                                sx={{
                                                    height: 48,
                                                    '&:hover': { backgroundColor: '#E0EBFF !important' },
                                                    '&.Mui-disabled': { backgroundColor: '#E0E0E0 !important', color: '#9E9E9E' },
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography sx={{ fontFamily: 'Roboto', fontSize: '16px', color: '#212121' }}>{row.index}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography sx={{ fontFamily: 'Roboto', fontSize: '16px', color: '#212121' }}>{row.email}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography sx={{ fontFamily: 'Roboto', fontSize: '16px', color: '#212121' }}>{row.phone}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography sx={{ fontFamily: 'Roboto', fontSize: '16px', color: '#212121' }}>{row.linkedAt}</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setStaffToDelete(row.original);
                                                            setDeleteDialogOpen(true);
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ flexGrow: 1 }} />

                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto', pt: 1, pb: 1 }}>
                                <Pagination
                                    count={Math.max(totalPages, 1)}
                                    page={currentPage}
                                    onChange={(_, value) => setCurrentPage(value)}
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            borderRadius: '50%',
                                            border: '1px solid #D1D5DB',
                                            width: 36,
                                            height: 36,
                                            margin: '0 4px',
                                            color: '#6B7280',
                                            '&:hover': { backgroundColor: '#E0EBFF' },
                                        },
                                        '& .Mui-selected': {
                                            backgroundColor: '#004DC2 !important',
                                            color: '#fff !important',
                                            border: 'none',
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* Add Staff Dialog - Create new staff account */}
            <Dialog
                open={showAddStaffModal}
                onClose={() => { setShowAddStaffModal(false); setCreateError(''); }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        padding: '24px',
                    }
                }}
            >
                <DialogTitle sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '20px', padding: 0, mb: 2 }}>
                    Create Staff Account
                </DialogTitle>
                <DialogContent sx={{ padding: 0 }}>
                    {createError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {createError}
                        </Alert>
                    )}

                    <Typography variant="body2" sx={{ mb: 2, color: '#666', fontFamily: 'Roboto' }}>
                        Create a new staff account. They will use these credentials to log in.
                    </Typography>

                    <TextField
                        fullWidth
                        label="Branch Name *"
                        placeholder="e.g. Thampanoor Branch"
                        value={staffBranchName}
                        onChange={(e) => setStaffBranchName(e.target.value)}
                        disabled={creatingLoading}
                        autoFocus
                        sx={{ ...textFieldStyles, mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Email *"
                        type="email"
                        placeholder="staff@example.com"
                        value={staffEmailInput}
                        onChange={(e) => setStaffEmailInput(e.target.value)}
                        disabled={creatingLoading}
                        sx={{ ...textFieldStyles, mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Password *"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Min 6 characters"
                        value={staffPasswordInput}
                        onChange={(e) => setStaffPasswordInput(e.target.value)}
                        disabled={creatingLoading}
                        sx={{ ...textFieldStyles, mb: 2 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <TextField
                        fullWidth
                        label="WhatsApp Number (Optional)"
                        placeholder="10-digit number"
                        value={staffWhatsApp}
                        onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val.length <= 10) setStaffWhatsApp(val);
                        }}
                        disabled={creatingLoading}
                        sx={textFieldStyles}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !creatingLoading) {
                                handleCreateStaff();
                            }
                        }}
                    />
                </DialogContent>

                <DialogActions sx={{ padding: 0, mt: 3, gap: 2 }}>
                    <Button
                        onClick={() => { setShowAddStaffModal(false); setCreateError(''); }}
                        disabled={creatingLoading}
                        sx={{
                            width: "120px",
                            height: "44px",
                            borderRadius: "8px",
                            borderColor: "#B00020",
                            color: "#B00020",
                            background: "#fff",
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0.01em",
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: "inherit",
                                color: "#B00020",
                                boxShadow: 10
                            }
                        }}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateStaff}
                        variant="contained"
                        disabled={creatingLoading || !staffBranchName.trim() || !staffEmailInput.trim() || !staffPasswordInput || staffPasswordInput.length < 6}
                        endIcon={creatingLoading && <CircularProgress size={20} />}
                        sx={{
                            minWidth: "140px",
                            height: "44px",
                            borderRadius: "8px",
                            backgroundColor: (creatingLoading || !staffBranchName.trim() || !staffEmailInput.trim() || !staffPasswordInput) ? "rgba(0, 0, 0, 0.12) !important" : "#0063F7 !important",
                            color: (creatingLoading || !staffBranchName.trim() || !staffEmailInput.trim() || !staffPasswordInput) ? "rgba(0, 0, 0, 0.26) !important" : "#FFFFFF !important",
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0.01em",
                            textTransform: "none",
                            boxShadow: "none",
                            '&:hover': {
                                backgroundColor: (creatingLoading || !staffBranchName.trim() || !staffEmailInput.trim() || !staffPasswordInput) ? "rgba(0, 0, 0, 0.12) !important" : "#0051d4 !important",
                                boxShadow: (creatingLoading || !staffBranchName.trim() || !staffEmailInput.trim() || !staffPasswordInput) ? "none" : 10
                            }
                        }}
                    >
                        Create Staff
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Dialog - Staff Created */}
            <Dialog
                open={successDialogOpen}
                onClose={() => {
                    setSuccessDialogOpen(false);
                    setCreatedStaffInfo(null);
                    setSendingInvitations(false);
                    setIsEmailLoading(false);
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        padding: '32px',
                        textAlign: 'center',
                    }
                }}
            >
                <DialogContent sx={{ padding: 0 }}>
                    <Box sx={{
                        width: 80, height: 80, mx: 'auto', mb: 3,
                        borderRadius: '50%', backgroundColor: '#E8F5E9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, fontFamily: 'Roboto' }}>
                        Staff Created Successfully!
                    </Typography>
                    <Typography sx={{ fontFamily: 'Roboto', fontSize: '14px', color: '#666', mb: 3, maxWidth: 420, mx: 'auto', lineHeight: 1.6 }}>
                        Please share these credentials with your staff so they can start managing policies.
                    </Typography>

                    {createdStaffInfo && (
                        <Box sx={{
                            mb: 3, mt: 1, textAlign: 'left'
                        }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#212121' }}>
                                Staff Credentials:
                            </Typography>
                            <Box sx={{
                                p: 2,
                                borderRadius: '8px',
                                backgroundColor: '#F5F5F5',
                                border: '1px solid #E0E0E0',
                                position: 'relative',
                            }}>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const text = `Branch: ${createdStaffInfo.branchName}\nEmail: ${createdStaffInfo.email}\nPassword: ${createdStaffInfo.password}`;
                                        navigator.clipboard.writeText(text);
                                        toast.info("Credentials copied to clipboard");
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        color: '#757575',
                                        '&:hover': { color: '#0063F7' },
                                    }}
                                >
                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#212121' }}>
                                    {createdStaffInfo.branchName}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#616161', display: 'block' }}>
                                    Email: {createdStaffInfo.email}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#616161', display: 'block' }}>
                                    Password: {createdStaffInfo.password}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', padding: 0, mt: 4, gap: 2, display: 'flex', width: '100%' }}>
                    <Button
                        onClick={() => {
                            setSuccessDialogOpen(false);
                            setCreatedStaffInfo(null);
                            setSendingInvitations(false);
                            setIsEmailLoading(false);
                        }}
                        variant="outlined"
                        sx={{
                            flex: 1,
                            height: '44px',
                            borderRadius: '8px',
                            borderColor: '#B00020',
                            color: '#B00020',
                            fontFamily: 'Roboto',
                            fontSize: '16px',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'inherit',
                                borderColor: '#B00020',
                                boxShadow: 10
                            }
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={isEmailLoading ? <CircularProgress size={18} color="inherit" /> : (sendingInvitations ? null : <EmailIcon />)}
                        onClick={handleShareCredentials}
                        disabled={sendingInvitations || isEmailLoading}
                        sx={{
                            flex: 1,
                            height: '44px',
                            borderRadius: '8px',
                            backgroundColor: sendingInvitations ? '#4CAF50 !important' : (isEmailLoading ? '#BDBDBD !important' : '#0063F7 !important'),
                            color: '#FFFFFF !important',
                            fontFamily: 'Roboto',
                            fontWeight: 500,
                            fontSize: '16px',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: sendingInvitations ? '#4CAF50 !important' : (isEmailLoading ? '#BDBDBD !important' : '#0051d4 !important'),
                                boxShadow: 10
                            },
                            '&.Mui-disabled': {
                                backgroundColor: sendingInvitations ? '#4CAF50 !important' : (isEmailLoading ? '#BDBDBD !important' : '#0063F7 !important'),
                                color: '#FFFFFF !important'
                            }
                        }}
                    >
                        {isEmailLoading ? 'Sharing...' : (sendingInvitations ? 'Sent ✓' : 'Share via Email')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog - matching ClientDetail style */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: {
                        width: "600px",
                        height: "192px",
                        backgroundColor: "#FFFFFF",
                        padding: "20px 32px",
                        boxShadow: "none",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        padding: 0,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "20px",
                            lineHeight: "24px",
                            color: "#212121",
                            textAlign: "left",
                            letterSpacing: "0.01em",
                            mt: "16px",
                        }}
                    >
                        Are you sure you want to delete {staffToDelete?.email}? This will permanently remove their account and all associated data.
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        justifyContent: "flex-end",
                        padding: 0,
                        marginTop: "16px",
                        gap: "20px",
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setStaffToDelete(null);
                        }}
                        sx={{
                            width: "120px",
                            height: "44px",
                            borderRadius: "8px",
                            borderColor: "#F04438",
                            color: "#F04438",
                            background: "#fff",
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0.01em",
                            textTransform: "none",
                            '&:hover': {
                                borderColor: "#F04438",
                                background: "#FFF3F2",
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            await handleDeleteStaff();
                        }}
                        sx={{
                            width: "120px",
                            height: "44px",
                            borderRadius: "8px",
                            backgroundColor: "#F04438 !important",
                            color: "#fff !important",
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0.01em",
                            textTransform: "none",
                            boxShadow: "none",
                            '&:hover': {
                                backgroundColor: "#D93C32 !important",
                            }
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={driveDisconnectDialogOpen}
                onClose={() => setDriveDisconnectDialogOpen(false)}
                PaperProps={{
                    sx: {
                        width: "600px",
                        height: "192px",
                        backgroundColor: "#FFFFFF",
                        padding: "20px 32px",
                        boxShadow: "none",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        padding: 0,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "20px",
                            lineHeight: "24px",
                            color: "#212121",
                            textAlign: "left",
                            letterSpacing: "0.01em",
                            mt: "16px",
                        }}
                    >
                        Are you sure you want to disconnect Google Drive?
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        justifyContent: "flex-end",
                        padding: 0,
                        marginTop: "16px",
                        gap: "20px",
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setDriveDisconnectDialogOpen(false);
                        }}
                        sx={{
                            width: "120px",
                            height: "44px",
                            borderRadius: "8px",
                            borderColor: "#F04438",
                            color: "#F04438",
                            background: "#fff",
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0.01em",
                            textTransform: "none",
                            '&:hover': {
                                borderColor: "#F04438",
                                background: "#FFF3F2",
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            await handleDisconnectDrive();
                        }}
                        disabled={driveLoading}
                        sx={{
                            width: "120px",
                            height: "44px",
                            borderRadius: "8px",
                            backgroundColor: "#F04438 !important",
                            color: "#fff !important",
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0.01em",
                            textTransform: "none",
                            boxShadow: "none",
                            '&:hover': {
                                backgroundColor: "#D93C32 !important",
                            }
                        }}
                    >
                        {driveLoading ? <CircularProgress size={20} color="inherit" /> : 'Disconnect'}
                    </Button>
                </DialogActions>
            </Dialog>


        </Box>
    );
};

export default StaffList;
